<template>
  <div class="font-sans bg-[#f8f9fa] min-h-screen text-[#191c1d]">

    <!-- Hero Section -->
    <section class="relative py-20 pb-24 overflow-hidden">
      <div class="absolute inset-0 pointer-events-none">
        <div class="absolute inset-0 bg-[#f8f9fa]"></div>
        <svg class="absolute top-0 right-0 w-2/3 h-full translate-x-1/4 -translate-y-1/4" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C300,800 400,1000 700,800 C1000,600 1000,0 1000,0 L0,0 Z" fill="#071c5b" opacity="0.2"/>
        </svg>
        <svg class="absolute bottom-0 left-0 w-full h-2/3" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C200,900 400,1000 600,800 C800,600 1000,700 1000,500 L1000,1000 Z" fill="#006e29" opacity="0.15"/>
        </svg>
        <div class="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#f5c343] opacity-20 blur-[100px]"></div>
        <div class="absolute bottom-20 right-20 w-80 h-80 rounded-full bg-[#d24340] opacity-[0.12] blur-[100px]"></div>
      </div>

      <div class="relative z-10 max-w-7xl mx-auto px-6 text-center">
        <h1 class="text-[clamp(2rem,5vw,3rem)] font-bold text-[#071c5b] tracking-tight leading-tight mb-4">
          {{ $t('samergo_home_hero_title') }}
        </h1>
        <p class="text-lg leading-relaxed text-[#454650] max-w-2xl mx-auto mb-12">
          {{ $t('samergo_home_hero_subtitle') }}
        </p>
        <div class="max-w-3xl mx-auto relative group">
          <div
            class="absolute -inset-1 z-0 bg-gradient-to-r from-[#7df98f] to-[#dde1ff] rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 pointer-events-none"></div>

          <!-- Prompt input — launches the chat modal -->
          <div
            class="relative tw-flex items-center bg-white/90 backdrop-blur-sm border border-[#c6c5d2] rounded-2xl p-2 shadow-sm cursor-pointer"
            @click="openChat"
          >
            <span class="flex-1 px-4 py-3 text-[#757681] text-sm text-left select-none">
              {{ $t('samergo_home_chat_placeholder') }}
            </span>
            <button class="bg-[#071c5b] text-white px-8 py-3 rounded-xl text-sm font-semibold whitespace-nowrap flex-shrink-0 hover:opacity-90 transition-opacity">
              {{ $t('samergo_home_chat_start') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Quick Actions -->
    <section class="-mt-12 relative z-20 pb-12">
      <div class="max-w-7xl mx-auto px-6">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            v-for="action in quickActions"
            :key="action.key"
            class="bg-white rounded-xl shadow-sm border-t-4 p-6 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all group"
            :style="{ borderTopColor: action.color }"
            @click="navigateTo(action.route)"
          >
            <div
              class="w-12 h-12 rounded-lg tw-flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
              :style="{ background: action.bgColor }"
            >
              <span class="material-symbols-outlined text-2xl" :style="{ color: action.iconColor }">{{ action.icon }}</span>
            </div>
            <h3 class="text-base font-semibold text-[#191c1d] mb-1">{{ action.title }}</h3>
            <p class="text-sm text-slate-500 leading-snug">{{ action.subtitle }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Network Map -->
    <section class="py-16">
      <div class="max-w-7xl mx-auto px-6">
        <div class="tw-flex items-center justify-between mb-6">
          <div class="tw-flex flex-col">
            <h2 class="text-3xl font-bold text-[#071c5b] leading-tight">{{ $t('samergo_home_network_title') }}</h2>
            <p class="text-sm text-[#757681] mt-1 mb-0">{{ $t('samergo_home_network_subtitle') }}</p>
          </div>
          <span class="tw-flex items-center gap-2 bg-[#edeeef] text-[#454650] text-xs font-semibold px-4 py-1.5 rounded-full">
            <span class="w-2 h-2 rounded-full bg-[#006e29] flex-shrink-0"></span>
            {{ $t('samergo_home_network_contacts', { count: networkContacts.length }) }}
          </span>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:h-[600px]">
          <!-- Contact list -->
          <div class="lg:col-span-5 tw-flex flex-col gap-4 overflow-y-auto pr-2 h-[320px] lg:h-auto">
            <!-- Skeletons while store is loading -->
            <template v-if="!networkContactsLoaded">
              <div v-for="n in 4" :key="n" class="bg-white p-4 rounded-xl border border-[#c6c5d2] tw-flex gap-4 pointer-events-none flex-shrink-0">
                <div class="w-16 h-16 rounded-lg flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="h-4 w-3/5 rounded shimmer"></div>
                  <div class="h-3 w-2/5 rounded shimmer"></div>
                  <div class="h-3 w-5/6 rounded shimmer"></div>
                </div>
              </div>
            </template>

            <!-- Empty state -->
            <template v-else-if="!networkContacts.length">
              <div class="tw-flex flex-col items-center justify-center h-full text-center py-12 gap-3">
                <span class="material-symbols-outlined text-[48px] text-[#c6c5d2]">hub</span>
                <p class="text-sm text-slate-400">{{ $t('samergo_home_network_empty') }}</p>
                <button
                  class="text-sm font-semibold text-[#071c5b] bg-transparent border-none cursor-pointer p-0 hover:underline"
                  @click="navigateTo('/actors-simplified')"
                >
                  {{ $t('samergo_home_network_empty_cta') }}
                </button>
              </div>
            </template>

            <!-- Contact cards (same card used on the My Network page) -->
            <template v-else>
              <SamergoNetworkCard
                v-for="contact in networkContacts"
                :key="contact.elastic_id"
                :contact="contact"
                :my-claims="myClaims"
              />
            </template>
          </div>

          <!-- Leaflet map -->
          <div class="lg:col-span-7 relative rounded-xl overflow-hidden shadow-sm border border-[#c6c5d2] h-[400px] lg:h-[600px]">
            <MapView
              v-if="networkContactsLoaded"
              :data="networkMapData"
              :getMapData="loadNetworkMapData"
              :getData="loadNetworkMapData"
              :noCluster="true"
              :noSearchWhileMoving="true"
              :disableMouseWheelZoom="true"
              :disableExpandButton="true"
              :fitBounds="mapFitBounds"
              :noLegend="true"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Smart Suggestions -->
    <section class="bg-[#f3f4f5] pt-10 pb-16">
      <div class="max-w-7xl mx-auto px-6">
        <h2 class="text-3xl font-bold text-[#071c5b] leading-tight mb-12">{{ $t('samergo_home_suggestions_title') }}</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Events card -->
          <div class="suggestions-card bg-white rounded-3xl p-6 shadow-sm border border-[#c6c5d2] overflow-hidden tw-flex flex-col gap-4">
            <div class="tw-flex flex-row justify-between">
              <span class="inline-block px-3 py-1 bg-[#dde1ff] text-[#324381] rounded-full text-sm font-semibold self-start">{{ $t('samergo_home_events_label') }}</span>
              <button
                class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#071c5b] bg-transparent border-none cursor-pointer p-0 hover:gap-4 transition-all duration-200"
                @click="navigateTo('/events')"
              >
                {{ $t('samergo_home_view_all') }}
                <span class="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>

            <!-- Loading skeletons -->
            <template v-if="eventsLoading">
              <div v-for="n in 3" :key="n" class="tw-flex gap-4 items-start py-3 border-b border-[#f0f0f0] last:border-0">
                <div class="w-10 h-10 rounded-lg flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="h-4 w-3/4 rounded shimmer"></div>
                  <div class="h-3 w-1/3 rounded shimmer"></div>
                </div>
              </div>
            </template>

            <!-- Events list -->
            <TransitionGroup v-else-if="events.length" name="stagger" tag="div" class="tw-flex flex-col">
              <div
                v-for="(event, i) in events"
                :key="event.id"
                class="event-row tw-flex gap-4 items-start py-3 border-b border-[#f0f0f0] last:border-0 cursor-pointer group rounded-xl px-2 -mx-2"
                :style="{ '--i': i }"
                @click="openAnnouncementModal(event)"
              >
                <div class="event-icon w-10 h-10 rounded-lg bg-[#dde1ff] tw-flex items-center justify-center flex-shrink-0 transition-transform duration-200">
                  <span class="material-symbols-outlined text-[20px] text-[#071c5b]">event</span>
                </div>
                <div class="tw-flex flex-col flex-1 min-w-0">
                  <p class="text-sm font-semibold text-[#191c1d] leading-snug group-hover:text-[#071c5b] transition-colors duration-150 mb-0">{{ event.title }}</p>
                  <!--                  <p v-if="event.description" class="text-xs text-[#454650] mt-0.5 line-clamp-1">{{ event.description }}</p>-->
                  <div class="tw-flex items-center gap-1.5 mt-1">
                    <template v-if="event.user?.name">
                      <div class="w-4 h-4 rounded-full overflow-hidden bg-[#dde1ff] tw-flex items-center justify-center flex-shrink-0">
                        <img v-if="event.user.photo_url" :src="event.user.photo_url" class="w-full h-full object-cover"/>
                        <span v-else class="text-[7px] font-bold text-[#071c5b] leading-none">{{ userInitials(event.user.name) }}</span>
                      </div>
                      <span class="text-[11px] text-slate-400 truncate">{{ event.user.name }}</span>
                      <span v-if="event.start_date" class="text-[11px] text-slate-300">·</span>
                    </template>
                    <span v-if="event.start_date" class="text-[11px] text-slate-400">{{ formatEventDate(event.start_date) }}</span>
                  </div>
                </div>
                <span
                  class="material-symbols-outlined text-[#c6c5d2] text-base self-center opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-150">chevron_right</span>
              </div>
            </TransitionGroup>

            <p v-else class="text-sm text-slate-400 py-4">{{ $t('samergo_home_events_empty') }}</p>

            <!--            <button
                          class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#071c5b] bg-transparent border-none cursor-pointer p-0 w-fit hover:gap-4 transition-all duration-200 mt-auto"
                          @click="navigateTo('/events')"
                        >
                          Alle evenementen
                          <span class="material-symbols-outlined">arrow_forward</span>
                        </button>-->
          </div>

          <!-- Announcements card -->
          <div class="suggestions-card bg-white rounded-3xl p-6 shadow-sm border border-[#c6c5d2] overflow-hidden tw-flex flex-col gap-4">
            <div class="tw-flex flex-row justify-between">
              <span class="inline-block px-3 py-1 bg-[#7df98f] text-[#00732b] rounded-full text-sm font-semibold self-start">{{ $t('samergo_home_announcements_label') }}</span>
              <button
                class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#071c5b] bg-transparent border-none cursor-pointer p-0 hover:gap-4 transition-all duration-200"
                @click="navigateTo('/announcements')"
              >
                {{ $t('samergo_home_view_all') }}
                <span class="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>


            <!-- Loading skeletons -->
            <template v-if="announcementsLoading">
              <div v-for="n in 3" :key="n" class="tw-flex gap-4 items-start py-3 border-b border-[#f0f0f0] last:border-0">
                <div class="w-10 h-10 rounded-lg flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="h-4 w-3/4 rounded shimmer"></div>
                  <div class="h-3 w-1/3 rounded shimmer"></div>
                </div>
              </div>
            </template>

            <!-- Announcements list -->
            <TransitionGroup v-else-if="announcements.length" name="stagger" tag="div" class="tw-flex flex-col">
              <div
                v-for="(item, i) in announcements"
                :key="item.id"
                class="announcement-row tw-flex gap-4 items-start py-3 border-b border-[#f0f0f0] last:border-0 cursor-pointer group rounded-xl px-2 -mx-2"
                :style="{ '--i': i }"
                @click="openAnnouncementModal(item)"
              >
                <div class="announcement-icon w-10 h-10 rounded-lg bg-[#eafff0] tw-flex items-center justify-center flex-shrink-0 transition-transform duration-200">
                  <span class="material-symbols-outlined text-[20px] text-[#006e29]">campaign</span>
                </div>
                <div class="tw-flex flex-col flex-1 min-w-0">
                  <p class="text-sm font-semibold text-[#191c1d] leading-snug group-hover:text-[#006e29] transition-colors duration-150 mb-0">{{ item.title }}</p>
                  <!--                  <p v-if="item.description" class="text-xs text-[#454650] mt-0.5 line-clamp-1">{{ item.description }}</p>-->
                  <div class="tw-flex items-center gap-1.5 mt-1">
                    <template v-if="item.user?.name">
                      <div class="w-4 h-4 rounded-full overflow-hidden bg-[#eafff0] tw-flex items-center justify-center flex-shrink-0">
                        <img v-if="item.user.photo_url" :src="item.user.photo_url" class="w-full h-full object-cover"/>
                        <span v-else class="text-[7px] font-bold text-[#006e29] leading-none">{{ userInitials(item.user.name) }}</span>
                      </div>
                      <span class="text-[11px] text-slate-400 truncate">{{ item.user.name }}</span>
                      <span v-if="item.created_at" class="text-[11px] text-slate-300">·</span>
                    </template>
                    <span v-if="item.created_at" class="text-[11px] text-slate-400">{{ formatEventDate(item.created_at) }}</span>
                  </div>
                </div>
                <span
                  class="material-symbols-outlined text-[#c6c5d2] text-base self-center opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-150">chevron_right</span>
              </div>
            </TransitionGroup>

            <p v-else class="text-sm text-slate-400 py-4">{{ $t('samergo_home_announcements_empty') }}</p>

            <!--            <button
                          class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#006e29] bg-transparent border-none cursor-pointer p-0 w-fit hover:gap-4 transition-all duration-200 mt-auto"
                          @click="navigateTo('/announcements-simplified')"
                        >
                          Alle aankondigingen
                          <span class="material-symbols-outlined">arrow_forward</span>
                        </button>-->
          </div>

          <!-- Dark quick access card: KB posts -->
          <div class="suggestions-card bg-[#071c5b] rounded-3xl p-6 tw-flex flex-col justify-between">
            <div>
              <div class="tw-flex flex-row justify-between">
                <span class="material-symbols-outlined text-[40px] text-[#f1c040] block kb-icon">menu_book</span>
                <button
                  class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-white bg-transparent border-none cursor-pointer p-0 hover:gap-4 transition-all duration-200"
                  @click="navigateTo('/dashboards/knowledge-base')"
                >
                  {{ $t('samergo_home_view_all') }}
                  <span class="material-symbols-outlined">arrow_forward</span>
                </button>
              </div>
              <h3 class="text-2xl font-bold text-white leading-tight mt-4 mb-2">{{ knowledgeBaseLabel }}</h3>
              <p class="text-sm text-blue-200/80 leading-relaxed">{{ $t('samergo_home_kb_subtitle') }}</p>
            </div>
            <TransitionGroup name="stagger" tag="ul" class="list-none p-0 mt-6 tw-flex flex-col gap-3">
              <template v-if="kbLoading">
                <li v-for="n in 4" :key="'sk-' + n" class="h-10 rounded-xl shimmer-dark"></li>
              </template>
              <template v-else-if="kbPosts.length">
                <li
                  v-for="(post, i) in kbPosts"
                  :key="post.id"
                  class="kb-row tw-flex items-start gap-3 bg-white/10 px-3 py-3 rounded-xl cursor-pointer text-white text-sm font-medium"
                  :style="{ '--i': i }"
                  @click="navigateTo('/knowledge-base/posts/' + post.id)"
                >
                  <span class="material-symbols-outlined flex-shrink-0 transition-transform duration-200">description</span>
                  <div class="tw-flex flex-col flex-1 min-w-0">
                    <span class="line-clamp-1">{{ post.title }}</span>
                    <div class="tw-flex items-center gap-1.5 mt-0.5">
                      <template v-if="post.creator?.name">
                        <div class="w-3.5 h-3.5 rounded-full overflow-hidden bg-white/20 tw-flex items-center justify-center flex-shrink-0">
                          <img v-if="post.creator.photoUrl" :src="post.creator.photoUrl" class="w-full h-full object-cover"/>
                          <span v-else class="text-[6px] font-bold text-white leading-none">{{ userInitials(post.creator.name) }}</span>
                        </div>
                        <span class="text-[11px] text-blue-200/70 truncate">{{ post.creator.name }}</span>
                        <span v-if="post.createdAt" class="text-[11px] text-blue-200/40">·</span>
                      </template>
                      <span v-if="post.createdAt" class="text-[11px] text-blue-200/50">{{ formatEventDate(post.createdAt) }}</span>
                    </div>
                  </div>
                  <span class="material-symbols-outlined text-sm opacity-0 kb-arrow transition-all duration-150">chevron_right</span>
                </li>
              </template>
              <li v-else key="empty" class="text-blue-200/60 text-sm px-3 py-2">{{ $t('samergo_home_kb_empty') }}</li>
            </TransitionGroup>
          </div>
        </div>
      </div>
    </section>

    <!-- New Actors in Network -->
    <section class="py-16">
      <div class="max-w-7xl mx-auto px-6">
        <div class="tw-flex items-center justify-between mb-12">
          <div class="tw-flex items-center gap-4">
            <div class="p-3 bg-[#7df98f] rounded-2xl tw-flex items-center justify-center">
              <span class="material-symbols-outlined text-[#00732b]">dynamic_feed</span>
            </div>
            <div>
              <h2 class="text-3xl font-bold text-[#071c5b] leading-tight">{{ $t('samergo_home_new_actors_title') }}</h2>
            </div>
          </div>
          <button
            class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#071c5b] bg-transparent border-none cursor-pointer p-0 hover:gap-4 transition-all duration-200"
            @click="navigateTo('/my-network')"
          >
            {{ $t('samergo_home_view_all') }}
            <span class="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Loading skeletons -->
          <template v-if="newActorsLoading">
            <div v-for="n in 6" :key="n" class="bg-white p-6 rounded-2xl border border-[#c6c5d2]">
              <div class="tw-flex items-center gap-3 mb-4">
                <div class="w-12 h-12 rounded-xl flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="h-4 w-3/4 rounded shimmer"></div>
                  <div class="h-3 w-1/2 rounded shimmer"></div>
                </div>
              </div>
              <div class="h-3 w-full rounded shimmer mb-2"></div>
              <div class="h-3 w-4/5 rounded shimmer mb-6"></div>
              <div class="h-8 w-32 rounded-lg shimmer"></div>
            </div>
          </template>

          <!-- Actor cards -->
          <template v-else-if="visibleNewActors.length">
            <SamergoActorCard
              v-for="actor in visibleNewActors"
              :key="actor.id"
              :actor="actor"
              :description="localizedDisplayProperty(actor, 'description') || localizedDisplayProperty(actor, 'short_description') || ''"
              :claimed-actors="myClaims"
              show-phone
            />
          </template>

          <!-- Empty state -->
          <p v-else class="col-span-3 text-sm text-slate-400 text-center py-12">
            {{ $t('samergo_home_new_actors_empty') }}
          </p>
        </div>
      </div>
    </section>

    <!-- New in your region -->
    <section class="bg-[#f3f4f5] py-16">
      <div class="max-w-7xl mx-auto px-6">
        <div class="tw-flex items-center justify-between mb-12">
          <div class="tw-flex items-center gap-4">
            <div class="p-3 bg-[#ffdf9a] rounded-2xl tw-flex items-center justify-center">
              <span class="material-symbols-outlined text-[#251a00]">location_on</span>
            </div>
            <div>
              <h2 class="text-3xl font-bold text-[#071c5b] leading-tight">{{ $t('samergo_home_region_title') }}</h2>
              <div class="tw-flex items-center gap-1.5 mt-1">
                <span class="text-sm text-[#757681]">{{ activeRegion ? activeRegion.label : $t('samergo_regio_no_location_title') }}</span>
                <button
                  class="tw-flex items-center justify-center w-6 h-6 rounded-md border border-[#c6c5d2] bg-white text-[#757681] hover:border-[#071c5b] hover:text-[#071c5b] transition-all cursor-pointer"
                  style="padding: 0;"
                  @click="openProfileModal"
                >
                  <span class="material-symbols-outlined" style="font-size: 13px;">edit</span>
                </button>
              </div>
            </div>
          </div>
          <button
            class="tw-flex items-center gap-2 font-bold text-[0.9rem] text-[#071c5b] bg-transparent border-none cursor-pointer p-0 hover:gap-4 transition-all duration-200"
            @click="navigateTo('/regio')"
          >
            {{ $t('samergo_home_view_all') }}
            <span class="material-symbols-outlined">arrow_forward</span>
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Loading skeletons -->
          <template v-if="regionActorsLoading">
            <div v-for="n in 6" :key="n" class="bg-white p-6 rounded-2xl border border-[#c6c5d2]">
              <div class="tw-flex items-center gap-3 mb-4">
                <div class="w-12 h-12 rounded-xl flex-shrink-0 shimmer"></div>
                <div class="tw-flex flex-col flex-1 gap-2">
                  <div class="h-4 w-3/4 rounded shimmer"></div>
                  <div class="h-3 w-1/2 rounded shimmer"></div>
                </div>
              </div>
              <div class="h-3 w-full rounded shimmer mb-2"></div>
              <div class="h-3 w-4/5 rounded shimmer mb-6"></div>
              <div class="h-8 w-32 rounded-lg shimmer"></div>
            </div>
          </template>

          <!-- Actor cards -->
          <template v-else-if="visibleRegionActors.length">
            <SamergoActorCard
              v-for="actor in visibleRegionActors"
              :key="actor.id"
              :actor="actor"
              :description="localizedDisplayProperty(actor, 'description') || localizedDisplayProperty(actor, 'short_description') || ''"
              :claimed-actors="myClaims"
            />
          </template>

          <p v-else class="col-span-3 text-sm text-slate-400 text-center py-12">
            {{ $t('samergo_home_region_empty') }}
          </p>
        </div>
      </div>
    </section>

    <SimplifiedFooterSamergo/>

    <!-- FAB -->
    <button
      class="fixed bottom-8 right-8 w-16 h-16 bg-[#071c5b] text-white border-none rounded-full shadow-[0_8px_24px_rgba(7,28,91,0.35)] tw-flex items-center justify-center z-50 cursor-pointer hover:scale-105 active:scale-95 transition-transform"
      @click="openAddActorModal"
    >
      <span class="material-symbols-outlined text-[28px]">add</span>
    </button>

    <!-- Announcement / Event modal -->
    <PersonalHomeAnnouncementModal
      v-if="activeAnnouncement"
      v-model="announcementModalOpen"
      :announcement="activeAnnouncement"
    />

    <!-- Chat modal — teleported so the whole conversation + input fit in one view (no page scroll) -->
    <Teleport to="body">
      <transition name="samergo-chat-modal">
        <div
          v-if="chatOpen"
          class="fixed inset-0 z-[100] tw-flex items-center justify-center p-4"
        >
          <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="chatOpen = false"></div>
          <div class="samergo-chat-modal__panel relative w-full max-w-2xl">
            <FacilityFinderChat
              ref="chatContainer"
              @close="chatOpen = false"
            />
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Owner picker — teleported to body to escape overflow/scroll clipping -->
    <Teleport to="body">
      <div
        v-if="ownerPickerActorId && ownerPickerPos"
        class="szw-popup"
        :style="{ top: ownerPickerPos.top + 'px', right: ownerPickerPos.right + 'px' }"
        @click.stop
      >
        <div class="szw-popup__header">
          <span class="material-symbols-outlined szw-popup__header-icon">hub</span>
          <span class="szw-popup__header-label">{{ $t('samergo_network_add_picker_title') }}</span>
        </div>
        <div class="szw-popup__options">
          <template v-for="claim in myClaims" :key="claim.actor_id">
            <button class="szw-popup__option" @click="addActivePickerActorToNetwork(claim.actor_id)">
              <span class="szw-popup__avatar">
                <img v-if="claim.logo" :src="claim.logo" class="szw-popup__avatar-img"/>
                <span v-else class="material-symbols-outlined">business</span>
              </span>
              <span class="szw-popup__option-name">{{ claim.name }}</span>
            </button>
          </template>
        </div>
      </div>
    </Teleport>

    <!-- Transfer picker — teleported to body to escape overflow/scroll clipping -->
    <Teleport to="body">
      <div
        v-if="transferPickerContactId && transferPickerPos"
        class="szw-popup"
        :style="{ top: transferPickerPos.top + 'px', right: transferPickerPos.right + 'px' }"
        @click.stop
      >
        <div class="szw-popup__header">
          <span class="material-symbols-outlined szw-popup__header-icon">swap_horiz</span>
          <span class="szw-popup__header-label">{{ $t('samergo_transfer_title') }}</span>
        </div>
        <div class="szw-popup__options">
          <template v-if="myClaims.filter(c => c.actor_id !== transferPickerOwnerActorId).length">
            <p class="szw-popup__section-label">{{ $t('samergo_transfer_move_to') }}</p>
            <template v-for="claim in myClaims" :key="'move-' + claim.actor_id">
              <button
                v-if="claim.actor_id !== transferPickerOwnerActorId"
                class="szw-popup__option"
                @click="transferActivePickerContact(claim.actor_id, false)"
              >
                <span class="szw-popup__avatar">
                  <img v-if="claim.logo" :src="claim.logo" class="szw-popup__avatar-img"/>
                  <span v-else class="material-symbols-outlined">business</span>
                </span>
                <span class="szw-popup__option-name">{{ claim.name }}</span>
              </button>
            </template>
            <div class="szw-popup__divider"></div>
            <p class="szw-popup__section-label">{{ $t('samergo_transfer_copy_to') }}</p>
            <template v-for="claim in myClaims" :key="'copy-' + claim.actor_id">
              <button v-if="claim.actor_id !== transferPickerOwnerActorId" class="szw-popup__option szw-popup__option--muted"
                      @click="transferActivePickerContact(claim.actor_id, true)">
                <span class="szw-popup__avatar szw-popup__avatar--copy">
                  <img v-if="claim.logo" :src="claim.logo" class="szw-popup__avatar-img szw-popup__avatar-img--muted"/>
                  <span v-else class="material-symbols-outlined">business</span>
                </span>
                <span class="szw-popup__option-name">{{ claim.name }}</span>
                <span class="material-symbols-outlined szw-popup__copy-icon">content_copy</span>
              </button>
            </template>
          </template>
          <p v-else class="szw-popup__empty">{{ $t('samergo_transfer_no_options') }}</p>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterSamergo from '@/components/SimplifiedFooter/SimplifiedFooterSamergo.vue'
  import SamergoActorCard from '@/components/Samergo/SamergoActorCard.vue'
  import SamergoNetworkCard from '@/components/Samergo/SamergoNetworkCard.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import UiMixin from '@/util/UiMixin'
  import { fetchPublicNotifications } from '@/api/notifications'
  import { fetchPosts as fetchKbPostsApi } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import { fetchNetworkActors } from '@/api/homepage'
  import { fetchActors, fetchContributors } from '@/api/actors'
  import { ACTION_TYPES as NETWORK_ACTION_TYPES } from '@/store/modules/network-contacts'
  import { fetchMyClaims } from '@/api/network-contacts'
  import MapView from '@/components/MapView/MapView.vue'
  import PersonalHomeAnnouncementModal from '@/components/Profile/PersonalHomeAnnouncementModal.vue'
  import FacilityFinderChat from '@/Domain/ConversationalAgent/Components/FacilityFinderChat.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '@/store/modules/filters'
  import MODAL_IDS from '@/constants/modal-ids'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'

  export default defineComponent({
    name: 'StaticHomeSamergoNew',
    mixins: [TranslationsMixin, UiMixin],
    components: {
      SimplifiedFooterSamergo,
      SamergoActorCard,
      SamergoNetworkCard,
      ActorAvatar,
      MapView,
      PersonalHomeAnnouncementModal,
      FacilityFinderChat,
    },
    data() {
      return {
        searchQuery: '', // kept for potential future use
        chatOpen: false,
        careProvidersLoading: true,
        quickActions: [
          {
            key: 'network',
            icon: 'hub',
            title: this.$t('samergo_home_action_network_title'),
            subtitle: this.$t('samergo_home_action_network_subtitle'),
            color: '#ffdad6',
            bgColor: '#ffdad6',
            iconColor: '#ba1a1a',
            route: '/my-network',
          },
          {
            key: 'professional',
            icon: 'corporate_fare',
            title: this.$t('samergo_home_action_professional_title'),
            subtitle: this.$t('samergo_home_action_professional_subtitle'),
            color: '#7df98f',
            bgColor: '#7df98f',
            iconColor: '#00732b',
            route: '/regio',
          },
          {
            key: 'patient',
            icon: 'person_search',
            title: this.$t('samergo_home_action_patient_title'),
            subtitle: this.$t('samergo_home_action_patient_subtitle'),
            color: '#071c5b',
            bgColor: '#dde1ff',
            iconColor: '#071c5b',
            route: '/actors-simplified?zhebar=1&portfolio=AekDJaLV24',
          },
          {
            key: 'regional',
            icon: 'map',
            title: this.$t('samergo_home_action_regional_title'),
            subtitle: this.$t('samergo_home_action_regional_subtitle'),
            color: '#f1c040',
            bgColor: '#ffdf9a',
            iconColor: '#251a00',
            route: '/actors-simplified?rond-zhebar=1&portfolio=LwVYMvrVDG',
          },
        ],
        careProviders: [],
        events: [],
        eventsLoading: true,
        announcements: [],
        announcementsLoading: true,
        kbPosts: [],
        kbLoading: true,
        newActors: [],
        newActorsLoading: true,
        regionActors: [],
        regionActorsLoading: false,
        addingToNetwork: {} as Record<string, boolean>,
        networkMapData: { companies: [], clusters: [] } as any,
        activeAnnouncement: null as any,
        announcementModalOpen: false,
        myClaims: [] as Array<{ actor_id: number; elastic_id: string; name: string; logo: string | null }>,
        ownerPickerActorId: null as string | null,
        ownerPickerActor: null as any,
        ownerPickerPos: null as { top: number; right: number } | null,
        transferPickerContactId: null as string | null,
        transferPickerOwnerActorId: null as number | null,
        transferPickerPos: null as { top: number; right: number } | null,
        contributorsMap: {} as Record<string, any[]>,
        mapDataSignature: '',
      }
    },
    computed: {
      language() {
        return this.$store.getters.activeLocale
      },
      activeRegion() {
        const REGIONS = [
          { key: 'albrandswaard', label: 'Albrandswaard', portfolio: 'lDnrgjNkQJ' },
          { key: 'barendrecht', label: 'Barendrecht', portfolio: 'JjVgNKmx2p' },
          { key: 'goeree-overflakkee', label: 'Goeree-Overflakkee', portfolio: '4EnmG5axQr' },
          { key: 'nissewaard', label: 'Nissewaard', portfolio: 'gXVp4APVQp' },
          { key: 'ridderkerk', label: 'Ridderkerk', portfolio: 'bGnGaejxNX' },
          { key: 'voorne-aan-zee', label: 'Voorne aan Zee', portfolio: 'GZx6lRjn8A' },
          { key: 'hoeksche-waard', label: 'Hoeksche-Waard', portfolio: 'RLn7EmEx0w' },
        ]
        return REGIONS.find(r => r.key === this.profile?.samergo_region) ?? null
      },
      quickActions() {
        return [
          {
            key: 'network',
            icon: 'hub',
            title: this.$t('samergo_home_action_network_title'),
            subtitle: this.$t('samergo_home_action_network_subtitle'),
            color: '#ffdad6',
            bgColor: '#ffdad6',
            iconColor: '#ba1a1a',
            route: '/my-network',
          },
          {
            key: 'professional',
            icon: 'corporate_fare',
            title: this.$t('samergo_home_action_professional_title'),
            subtitle: this.$t('samergo_home_action_professional_subtitle'),
            color: '#7df98f',
            bgColor: '#7df98f',
            iconColor: '#00732b',
            route: '/regio',
          },
          {
            key: 'patient',
            icon: 'person_search',
            title: this.$t('samergo_home_action_patient_title'),
            subtitle: this.$t('samergo_home_action_patient_subtitle'),
            color: '#071c5b',
            bgColor: '#dde1ff',
            iconColor: '#071c5b',
            route: '/actors-simplified?zhebar=1&portfolio=AekDJaLV24',
          },
          {
            key: 'regional',
            icon: 'map',
            title: this.$t('samergo_home_action_regional_title'),
            subtitle: this.$t('samergo_home_action_regional_subtitle'),
            color: '#f1c040',
            bgColor: '#ffdf9a',
            iconColor: '#251a00',
            route: '/actors-simplified?rond-zhebar=1&portfolio=LwVYMvrVDG',
          },
        ]
      },
      networkContacts() {
        return this.$store.getters.networkContacts
      },
      networkContactsLoaded() {
        return this.$store.getters.networkContactsLoaded
      },
      networkContactsWithLocation() {
        return this.networkContacts.filter((c: any) => c.location?.lat && c.location?.lon)
      },
      // Maps every domain/industry value (parent or child) to its top-level ancestor id.
      // The map legend lookup only carries parent-level colours, so child-tagged actors
      // must be coloured by their parent; the card label still shows the child term.
      legendAncestorMap(): Record<string, any> {
        const map: Record<string, any> = {}
        const walk = (list: any[], childrenKey: string) => {
          if (!Array.isArray(list)) return
          list.forEach((top: any) => {
            map[top.id] = top.id
            const children = top[childrenKey]
            if (Array.isArray(children)) children.forEach((child: any) => {
              map[child.id] = top.id
            })
          })
        }
        walk(this.$store.state.taxonomies.domains, 'domains')
        walk(this.$store.state.taxonomies.industries, 'industries')
        return map
      },
      mapFitBounds() {
        // Fit the map to the visible network actor markers
        return this.networkContactsWithLocation.length > 0
      },
      networkContactsMap(): Record<string, any> {
        const map: Record<string, any> = {}
        this.networkContacts.forEach((c: any) => {
          map[c.elastic_id] = c
        })
        return map
      },
      taxonomyIdToLabel(): Record<string, string> {
        const map: Record<string, string> = {}
        const add = (list: Array<{ value: any; label: string }>) => {
          if (!list) return
          list.forEach(item => {
            map[String(item.value)] = item.label
          })
        }
        add(this.$store.getters.categoryValuesAndLabels)
        add(this.$store.getters.activityValuesAndLabels)
        add(this.$store.getters.domainValuesAndLabels)
        add(this.$store.getters.technologyValuesAndLabels)
        add(this.$store.getters.membershipValuesAndLabels)
        return map
      },
      visibleNewActors(): any[] {
        const len = this.newActors.length
        const full = Math.floor(len / 3) * 3
        return this.newActors.slice(0, full || len)
      },
      visibleRegionActors(): any[] {
        const len = this.regionActors.length
        const full = Math.floor(len / 3) * 3
        return this.regionActors.slice(0, full || len)
      },
    },
    async mounted() {
      // Populate the map's colour legend lookup — this page doesn't mount any FiltersMixin
      // component, so without this the markers fall back to the grey default icon.
      this.initLegend()
      await this.$store.dispatch(NETWORK_ACTION_TYPES.FETCH)
      this.loadNetworkMapData()
      this.networkContacts.forEach((c: any) => this.loadContributors(c.elastic_id))

      this.fetchEvents()
      this.fetchAnnouncements()
      this.fetchKbPosts()
      this.fetchNewActors()
      this.fetchRegionActors()
      this.loadMyClaims()
      document.addEventListener('click', this.closePickers)
    },
    beforeUnmount() {
      document.removeEventListener('click', this.closePickers)
    },
    watch: {
      networkContacts(contacts: any[]) {
        this.loadNetworkMapData()
        contacts.forEach((c: any) => this.loadContributors(c.elastic_id))
      },
      activeRegion() {
        this.fetchRegionActors()
      },
    },
    methods: {
      openProfileModal() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.EDIT_USER_PROFILE)
      },
      initLegend() {
        const config = this.$store.state.config
        const property = config.legendProperty || config.legendeProperty || 'category'
        const getterByProperty: Record<string, string> = {
          domains: 'domainValuesAndLabels',
          industries: 'industryValuesAndLabels',
          category: 'categoryValuesAndLabels',
          activities: 'activityValuesAndLabels',
          membership: 'membershipValuesAndLabels',
          technology: 'technologyValuesAndLabels',
          stage: 'stageValuesAndLabels',
        }
        const getter = getterByProperty[property]
        const items = getter ? this.$store.getters[getter] : []
        this.$store.commit(FILTERS_MUTATION_TYPES.SET_LEGEND_PROPERTY, { property, items })
      },
      openAddActorModal() {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
      },
      contributorEntries(contact: any): Array<{ phone: string; notes: string }> {
        return Object.values(contact?.contributors_data ?? {}) as Array<{ phone: string; notes: string }>
      },
      contributorPhone(contact: any, userId: number): string {
        return (contact?.contributors_data ?? {})[userId]?.phone ?? ''
      },
      async loadContributors(elasticId: string) {
        if (this.contributorsMap[elasticId] !== undefined) return
        try {
          const res = await fetchContributors(elasticId, { includeOwners: true })
          this.contributorsMap = { ...this.contributorsMap, [elasticId]: Array.isArray(res) ? res : [] }
        } catch {
          this.contributorsMap = { ...this.contributorsMap, [elasticId]: [] }
        }
      },
      navigateTo(route: string) {
        this.$router.push(route)
      },
      openAnnouncementModal(item: any) {
        this.activeAnnouncement = item
        this.announcementModalOpen = true
      },
      async openChat() {
        this.chatOpen = true
        await this.$nextTick()
        const chat = this.$refs.chatContainer as any
        chat?.focusInput?.()
      },
      async fetchEvents() {
        this.eventsLoading = true
        try {
          const res = await fetchPublicNotifications(5, 0, { is_event: [1] })
          this.events = Array.isArray(res) ? res : (res?.data ?? res?.results ?? [])
        } catch {
          // keep empty
        } finally {
          this.eventsLoading = false
        }
      },
      async fetchAnnouncements() {
        this.announcementsLoading = true
        try {
          const res = await fetchPublicNotifications(5, 0, { is_event: false })
          this.announcements = Array.isArray(res) ? res : (res?.data ?? res?.results ?? [])
        } catch {
          // keep empty
        } finally {
          this.announcementsLoading = false
        }
      },
      async fetchKbPosts() {
        this.kbLoading = true
        try {
          const res = await fetchKbPostsApi({ per_page: 5 })
          console.log(res)
          this.kbPosts = Array.isArray(res?.data.data.posts) ? res.data.data.posts : []
        } catch {
          // keep empty
        } finally {
          this.kbLoading = false
        }
      },
      async fetchNewActors() {
        this.newActorsLoading = true
        try {
          const actors = await fetchNetworkActors({ limit: 6 })
          this.newActors = Array.isArray(actors) ? actors : []
        } catch {
          // keep empty
        } finally {
          this.newActorsLoading = false
        }
      },
      async fetchRegionActors() {
        if (!this.activeRegion) {
          this.regionActors = []
          return
        }
        this.regionActorsLoading = true
        try {
          const response = await fetchActors({ portfolio: this.activeRegion.portfolio, limit: 6 })
          this.regionActors = Array.isArray(response?.data) ? response.data : []
        } catch {
          // keep empty
        } finally {
          this.regionActorsLoading = false
        }
      },
      async loadMyClaims() {
        try {
          this.myClaims = await fetchMyClaims()
        } catch {
          // non-critical
        }
      },
      isInNetwork(actorId: string) {
        return this.$store.getters['isNetworkContact'](actorId)
      },
      openOwnerPicker(actor: any, event: MouseEvent) {
        if (this.ownerPickerActorId === actor.id) {
          this.closePickers()
          return
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
        this.ownerPickerActorId = actor.id
        this.ownerPickerActor = actor
        this.ownerPickerPos = { top: rect.bottom + 6, right: window.innerWidth - rect.right }
        this.transferPickerContactId = null
        this.transferPickerPos = null
      },
      async addActivePickerActorToNetwork(ownerActorId: number | null) {
        const actor = this.ownerPickerActor
        this.closePickers()
        if (actor) await this.addToNetworkAs(actor, ownerActorId)
      },
      openTransferPicker(elasticId: string, event: MouseEvent) {
        if (this.transferPickerContactId === elasticId) {
          this.closePickers()
          return
        }
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
        const contact = this.networkContacts.find((c: any) => c.elastic_id === elasticId)
        this.transferPickerContactId = elasticId
        this.transferPickerOwnerActorId = contact?.owner_actor_id ?? null
        this.transferPickerPos = { top: rect.bottom + 6, right: window.innerWidth - rect.right }
        this.ownerPickerActorId = null
        this.ownerPickerPos = null
      },
      closePickers() {
        this.ownerPickerActorId = null
        this.ownerPickerActor = null
        this.ownerPickerPos = null
        this.transferPickerContactId = null
        this.transferPickerOwnerActorId = null
        this.transferPickerPos = null
      },
      async transferActivePickerContact(ownerActorId: number, duplicate: boolean) {
        const contact = this.networkContacts.find((c: any) => c.elastic_id === this.transferPickerContactId)
        if (!contact) return
        this.closePickers()
        await this.transferContact(contact, ownerActorId, duplicate)
      },
      async addToNetworkAs(actor: any, ownerActorId: number | null) {
        this.closePickers()
        if (this.addingToNetwork[actor.id] || this.isInNetwork(actor.id)) return
        this.addingToNetwork = { ...this.addingToNetwork, [actor.id]: true }
        try {
          await this.$store.dispatch(NETWORK_ACTION_TYPES.ADD_CONTACT, {
            elasticId: actor.id,
            name: actor.name,
            image: actor.logo ?? null,
            ownerActorId,
          })
        } catch {
          // silently fail
        } finally {
          this.addingToNetwork = { ...this.addingToNetwork, [actor.id]: false }
        }
      },
      addToNetwork(actor: any, event: MouseEvent) {
        if (this.myClaims.length === 1) {
          this.addToNetworkAs(actor, this.myClaims[0].actor_id)
        } else if (this.myClaims.length > 1) {
          this.openOwnerPicker(actor, event)
        } else {
          // Users need an active claim before they can add actors to their social map
          this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, { actorRequiredVariant: 'network' })
          this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ACTOR_CLAIM_REQUIRED)
        }
      },
      async transferContact(contact: any, ownerActorId: number | null, duplicate = false) {
        this.closePickers()
        try {
          await this.$store.dispatch(NETWORK_ACTION_TYPES.TRANSFER_CONTACT, {
            elasticId: contact.elastic_id,
            ownerActorId,
            duplicate,
          })
        } catch {
          // silently fail
        }
      },
      userInitials(name: string): string {
        if (!name) return '?'
        const parts = name.trim().split(/\s+/)
        return parts.length >= 2
          ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
          : name.slice(0, 2).toUpperCase()
      },
      formatEventDate(dateStr: string) {
        if (!dateStr) return ''
        const d = new Date(dateStr)
        return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
      },
      loadNetworkMapData() {
        const companies = this.networkContacts
          .filter((c: any) => c.location?.lat && c.location?.lon)
          .map((c: any) => ({
            id: c.elastic_id,
            name: c.name,
            location: c.location,
            // Colour markers by the top-level taxonomy ancestor (legend lookup is parent-keyed)
            legend: c.legend != null ? (this.legendAncestorMap[c.legend] ?? c.legend) : null,
            category: c.role ?? null,
          }))
        // Only rebuild the map data when the markers actually change (id / location / legend).
        // Contact edits like saving a note must not re-render the map or re-fit the bounds.
        const signature = companies
          .map((c: any) => `${c.id}:${c.location.lat},${c.location.lon}:${c.legend}`)
          .join('|')
        if (signature === this.mapDataSignature) return
        this.mapDataSignature = signature
        this.networkMapData = { companies, clusters: [] }
      },
    },
  })
</script>

<style scoped>
  @keyframes shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .shimmer {
    background: linear-gradient(90deg, #e8e9ec 25%, #f3f4f5 50%, #e8e9ec 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
  }

  .shimmer-dark {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 25%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.08) 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
  }

  /* Card entrance */
  .suggestions-card {
    animation: fade-up 0.5s ease both;
  }

  .suggestions-card:nth-child(2) {
    animation-delay: 0.1s;
  }

  /* Staggered list entrance */
  .stagger-enter-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
    transition-delay: calc(var(--i, 0) * 60ms);
  }

  .stagger-enter-from {
    opacity: 0;
    transform: translateY(8px);
  }

  /* Event row hover */
  .event-row:hover {
    background: #f8f9ff;
  }

  .event-row:hover .event-icon {
    transform: scale(1.1) rotate(-4deg);
    background: #c8ceff;
  }

  /* Announcement row hover */
  .announcement-row:hover {
    background: #f0fff4;
  }

  .announcement-row:hover .announcement-icon {
    transform: scale(1.1) rotate(-4deg);
    background: #c6f7d4;
  }

  /* KB row hover */
  .kb-row {
    transition: background 0.15s ease, transform 0.15s ease;
  }

  .kb-row:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateX(3px);
  }

  .kb-row:hover .kb-arrow {
    opacity: 1;
  }

  .kb-row:active {
    transform: translateX(3px) scale(0.98);
  }

  /* KB icon float */
  .kb-icon {
    animation: float 3s ease-in-out infinite;
  }

  @keyframes fade-up {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-5px);
    }
  }

  /* Actor card entrance */
  .actor-card {
    animation: fade-up 0.45s ease both;
    animation-delay: calc(var(--i, 0) * 50ms);
  }
</style>

<style lang="scss">
  /* Chat modal transition — unscoped because the modal is teleported to <body> */
  .samergo-chat-modal-enter-active,
  .samergo-chat-modal-leave-active {
    transition: opacity 0.25s ease;
  }

  .samergo-chat-modal-enter-active .samergo-chat-modal__panel,
  .samergo-chat-modal-leave-active .samergo-chat-modal__panel {
    transition: transform 0.25s ease, opacity 0.25s ease;
  }

  .samergo-chat-modal-enter-from,
  .samergo-chat-modal-leave-to {
    opacity: 0;
  }

  .samergo-chat-modal-enter-from .samergo-chat-modal__panel,
  .samergo-chat-modal-leave-to .samergo-chat-modal__panel {
    transform: translateY(12px) scale(0.98);
    opacity: 0;
  }

  /* Shared picker popup — unscoped so Teleport renders can be targeted */
  .szw-popup {
    position: fixed;
    z-index: 9999;
    background: white;
    border: 1px solid #e2e4ea;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(7, 28, 91, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
    min-width: 220px;
    overflow: hidden;
    font-family: 'Plus Jakarta Sans', Inter, sans-serif;
  }

  .szw-popup__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px 12px;
    border-bottom: 1px solid #f0f1f5;
    background: #f8f9ff;
  }

  .szw-popup__header-icon {
    font-size: 16px;
    color: #071c5b;
    opacity: 0.7;
  }

  .szw-popup__header-label {
    font-size: 0.78rem;
    font-weight: 700;
    color: #071c5b;
    letter-spacing: -0.01em;
  }

  .szw-popup__options {
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .szw-popup__section-label {
    font-size: 0.68rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 6px 10px 2px;
    margin: 0;
  }

  .szw-popup__divider {
    height: 1px;
    background: #f0f1f5;
    margin: 4px 0;
  }

  .szw-popup__option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 10px;
    border: none;
    background: transparent;
    cursor: pointer;
    width: 100%;
    text-align: left;
    transition: background 0.12s;

    &:hover {
      background: #f3f4f8;
    }

    &:active {
      background: #e8eaf2;
    }

    &--muted .szw-popup__option-name {
      color: #454650;
    }
  }

  .szw-popup__avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: #071c5b;

    .material-symbols-outlined {
      font-size: 17px;
    }

    &--personal {
      background: #f0f1f5;
      color: #757681;
    }

    &--copy {
      opacity: 0.6;
    }
  }

  .szw-popup__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;

    &--muted {
      opacity: 0.6;
    }
  }

  .szw-popup__option-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #191c1d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }

  .szw-popup__copy-icon {
    font-size: 14px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  .szw-popup__empty {
    font-size: 0.8rem;
    color: #94a3b8;
    font-style: italic;
    text-align: center;
    padding: 12px 16px;
    margin: 0;
  }
</style>
